import {
  keyAttr,
  keyMapItem,
  LayerKeysMap,
  DualModeKey,
  fnKeyType,
  DKS,
  RS,
  SOCD,
  Macro,
  macro,
  mouseKey,
} from '../../../../types/keyboardKeys'
import { KeyboardKeysFeature } from '../../../../features/keyboard-keys/contract'
import { platform } from '../../../../types/config'
import { CMD_OHID, OHID_CMD_WRITE, PROTOCOL_CONFIG } from '../../protocols/cmd'
import { calculateChecksum } from '../../protocols/common'

const MASTER_REPORT_ID = PROTOCOL_CONFIG.master_report_id
const REPORT_ID = PROTOCOL_CONFIG.report_id

/**
 * @file OHIDKeys.ts
 * @author Rapoo Device SDK Team
 * @date 2026-06-11
 * @description OHIDKeys 键位功能实现
 */
export class OHIDKeyboardKeys extends KeyboardKeysFeature {
  private ohidDriverParam(options: {
    write: boolean
    mKey: number[]
    item: number[]
    value: number[]
    len: number
  }): ArrayBuffer {
    const buffer = new ArrayBuffer(64 * 4)
    const view = new DataView(buffer)

    const cmd = options.write
      ? OHID_CMD_WRITE(CMD_OHID.ohid_cmd_driver_param)
      : CMD_OHID.ohid_cmd_driver_param
    let offset = 0

    // 写入头部
    view.setUint8(offset++, MASTER_REPORT_ID) // HEAD
    view.setUint8(offset++, 0) // length
    view.setUint8(offset++, cmd) // cmd
    view.setUint8(offset++, 0) // checksum

    for (let i = 0; i < options.len; i++) {
      view.setUint16(offset, options.mKey[i] || 0, true)
      offset += 2
      view.setUint8(offset, options.item[i] || 0)
      offset++
      view.setUint16(offset, options.value[i] || 0, true)
      offset += 2
    }

    // 更新长度
    const dataLength = offset - 4
    view.setUint8(1, dataLength)

    const checksum = calculateChecksum(view, dataLength, 4)

    view.setUint8(3, checksum & 0xff)

    return buffer.slice(0, offset)
  }

  /**
   * ohidDriverParam 函数的逆函数
   * 从 ArrayBuffer 解析驱动参数配置
   *
   * @param buffer - ArrayBuffer 数据包
   * @param len - 数据项的长度
   * @returns 解析出的 options 参数
   */
  private parseDriverParamOptions(
    buffer: ArrayBuffer,
    len: number,
  ): {
    write: boolean
    mKey: number[]
    item: number[]
    value: number[]
  } {
    const view = new DataView(buffer)
    let offset = 0

    // 1. 解析头部
    const HEAD = view.getUint8(offset++) // 协议头
    const dataLength = view.getUint8(offset++) // 数据长度
    const cmd = view.getUint8(offset++) // 命令
    view.getUint8(offset++) // 校验和

    // 2. 验证协议头
    if (HEAD !== 0xa5) {
      throw new Error(`无效的协议头: 期望 0xa5, 实际 0x${HEAD.toString(16)}`)
    }

    // 3. 解析写入/读取标志
    const isWrite = (cmd & 0x80) !== 0
    const baseCmd = cmd & 0x7f

    // 验证命令是否为驱动参数命令
    if (baseCmd !== CMD_OHID.ohid_cmd_driver_param) {
      throw new Error(
        `无效的命令: 期望 0x${CMD_OHID.ohid_cmd_driver_param.toString(16)}, 实际 0x${baseCmd.toString(16)}`,
      )
    }

    // 4. 验证数据长度
    const expectedDataLength = len * 5 // 每个数据项: mKey(2字节) + item(1字节) + value(2字节) = 5字节
    if (dataLength < expectedDataLength) {
      throw new Error(`数据长度不匹配: 期望 ${expectedDataLength}, 实际 ${dataLength}`)
    }

    // 5. 解析数据数组
    const mKey: number[] = []
    const item: number[] = []
    const value: number[] = []

    for (let i = 0; i < len; i++) {
      if (offset + 5 > view.byteLength) {
        throw new Error(`缓冲区越界: 尝试读取位置 ${offset}, 但缓冲区长度只有 ${view.byteLength}`)
      }

      mKey.push(view.getUint16(offset, true))
      offset += 2

      item.push(view.getUint8(offset))
      offset++

      value.push(view.getUint16(offset, true))
      offset += 2
    }

    return {
      write: isWrite,
      mKey,
      item,
      value,
    }
  }

  /**
   * 生成MT驱动参数数据包
   *
   * @param options - 参数选项
   * @returns ArrayBuffer 数据包
   */
  private ohidDriverMT(options: {
    write: boolean
    mKey: number[]
    key1: number[]
    key2: number[]
    delay: number[]
    len: number
  }): ArrayBuffer {
    const buffer = new ArrayBuffer(64 * 4)
    const view = new DataView(buffer)

    const cmd = options.write
      ? OHID_CMD_WRITE(CMD_OHID.ohid_cmd_driver_mt)
      : CMD_OHID.ohid_cmd_driver_mt
    let offset = 0

    // 写入头部
    view.setUint8(offset++, MASTER_REPORT_ID) // HEAD
    view.setUint8(offset++, 0) // length
    view.setUint8(offset++, cmd) // cmd
    view.setUint8(offset++, 0) // checksum

    // 写入数据：每个 MT 项包含 4 个 16 位值（共 8 字节）
    for (let i = 0; i < options.len; i++) {
      view.setUint16(offset, options.mKey[i] || 0, true)
      offset += 2
      view.setUint16(offset, options.key1[i] || 0, true)
      offset += 2
      view.setUint16(offset, options.key2[i] || 0, true)
      offset += 2
      view.setUint16(offset, options.delay[i] || 0, true)
      offset += 2
    }

    // 更新长度
    const dataLength = offset - 4
    view.setUint8(1, dataLength)

    // 计算校验和
    const checksum = calculateChecksum(view, dataLength, 4)
    view.setUint8(3, checksum & 0xff)

    return buffer.slice(0, offset)
  }

  /**
   * ohidDriverMT 的逆函数
   * 从 ArrayBuffer 解析 MT 驱动参数配置
   *
   * @param buffer - ArrayBuffer 数据包
   * @param len - 数据项的长度
   * @returns 解析出的 options 参数
   */
  // private _parseDriverMTOptions(
  //   buffer: ArrayBuffer,
  //   len: number,
  // ): {
  //   write: boolean
  //   mKey: number[]
  //   key1: number[]
  //   key2: number[]
  //   delay: number[]
  // } {
  //   const view = new DataView(buffer)
  //   let offset = 0

  //   // 1. 解析头部
  //   const HEAD = view.getUint8(offset++) // 协议头
  //   const dataLength = view.getUint8(offset++) // 数据长度
  //   const cmd = view.getUint8(offset++) // 命令
  //   //const checksum = view.getUint8(offset++) // 校验和
  //   offset++

  //   // 2. 验证协议头
  //   if (HEAD !== 0xa5) {
  //     throw new Error(`无效的协议头: 期望 0xa5, 实际 0x${HEAD.toString(16)}`)
  //   }

  //   // 3. 解析写入/读取标志
  //   const isWrite = (cmd & 0x80) !== 0
  //   const baseCmd = cmd & 0x7f

  //   // 验证命令是否为 MT 驱动参数命令
  //   if (baseCmd !== CMD_OHID.ohid_cmd_driver_mt) {
  //     throw new Error(
  //       `无效的命令: 期望 0x${CMD_OHID.ohid_cmd_driver_mt.toString(16)}, 实际 0x${baseCmd.toString(16)}`,
  //     )
  //   }

  //   // 4. 验证数据长度
  //   const expectedDataLength = len * 8 // 每个 MT 项: mKey(2字节) + key1(2字节) + key2(2字节) + delay(2字节) = 8字节
  //   if (dataLength < expectedDataLength) {
  //     throw new Error(`数据长度不匹配: 期望 ${expectedDataLength}, 实际 ${dataLength}`)
  //   }

  //   // 5. 解析数据数组
  //   const mKey: number[] = []
  //   const key1: number[] = []
  //   const key2: number[] = []
  //   const delay: number[] = []

  //   for (let i = 0; i < len; i++) {
  //     if (offset + 8 > view.byteLength) {
  //       throw new Error(`缓冲区越界: 尝试读取位置 ${offset}, 但缓冲区长度只有 ${view.byteLength}`)
  //     }

  //     mKey.push(view.getUint16(offset, true))
  //     offset += 2

  //     key1.push(view.getUint16(offset, true))
  //     offset += 2

  //     key2.push(view.getUint16(offset, true))
  //     offset += 2

  //     delay.push(view.getUint16(offset, true))
  //     offset += 2
  //   }

  //   return {
  //     write: isWrite,
  //     mKey,
  //     key1,
  //     key2,
  //     delay,
  //   }
  // }
  /**
   *
   * @param mKey 获取单个按键的MT数据
   * @returns
   */
  async getDriverMT(mKey: number): Promise<DataView> {
    const requestOptions = {
      write: false,
      mKey: Array(1).fill(mKey),
      key1: Array(1).fill(0),
      key2: Array(1).fill(0),
      delay: [0],
      len: 1,
    }
    const outputData = new Uint8Array(this.ohidDriverMT(requestOptions))
    const response = await this.device.sendAndReceive(REPORT_ID, outputData, REPORT_ID, 500)
    return response.data
  }

  /**
   *
   * @param mKey 获取单个按键的TGL数据
   * @returns
   */
  async getDriverTGL(mKey: number): Promise<DataView> {
    const requestOptions = {
      write: false,
      mKey: Array(1).fill(mKey),
      key1: Array(1).fill(0),
      delay: [0],
      len: 1,
    }
    const outputData = new Uint8Array(this.ohidDriverTGL(requestOptions))
    const response = await this.device.sendAndReceive(REPORT_ID, outputData, REPORT_ID, 500)
    return response.data
  }

  /**
   *
   * @param mKey 获取单个按键的DKS数据
   * @returns
   */
  async getDriverDKS(mKey: number): Promise<DataView> {
    const requestOptions = {
      write: false,
      mKey: Array(1).fill(mKey),
      key1: Array(1).fill(0),
      key2: Array(1).fill(0),
      key3: Array(1).fill(0),
      key4: Array(1).fill(0),
      TrPs1: Array(1).fill(0),
      TrPs2: Array(1).fill(0),
      TrPs3: Array(1).fill(0),
      TrPs4: Array(1).fill(0),
      mm1: Array(1).fill(0),
      len: 1,
    }
    const outputData = new Uint8Array(this.ohidDriverDKS(requestOptions))
    const response = await this.device.sendAndReceive(REPORT_ID, outputData, REPORT_ID, 500)
    return response.data
  }

  /**
   * 生成 TGL (Toggle) 驱动参数数据包
   *
   * @param options - 参数选项
   * @returns ArrayBuffer 数据包
   */
  private ohidDriverTGL(options: {
    write: boolean
    mKey: number[]
    key1: number[]
    delay: number[]
    len: number
  }): ArrayBuffer {
    const buffer = new ArrayBuffer(64 * 4)
    const view = new DataView(buffer)

    const cmd = options.write
      ? OHID_CMD_WRITE(CMD_OHID.ohid_cmd_driver_tgl)
      : CMD_OHID.ohid_cmd_driver_tgl
    let offset = 0

    // 写入头部
    view.setUint8(offset++, MASTER_REPORT_ID) // HEAD
    view.setUint8(offset++, 0) // length
    view.setUint8(offset++, cmd) // cmd
    view.setUint8(offset++, 0) // checksum

    // 写入数据：每个 TGL 项包含 3 个 16 位值（共 6 字节）
    for (let i = 0; i < options.len; i++) {
      view.setUint16(offset, options.mKey[i] || 0, true)
      offset += 2
      view.setUint16(offset, options.key1[i] || 0, true)
      offset += 2
      view.setUint16(offset, options.delay[i] || 0, true)
      offset += 2
    }

    // 更新长度
    const dataLength = offset - 4
    view.setUint8(1, dataLength)

    // 计算校验和
    const checksum = calculateChecksum(view, dataLength, 4)
    view.setUint8(3, checksum & 0xff)

    return buffer.slice(0, offset)
  }

  /**
   * ohidDriverTGL 的逆函数
   * 从 ArrayBuffer 解析 TGL 驱动参数配置
   *
   * @param buffer - ArrayBuffer 数据包
   * @param len - 数据项的长度
   * @returns 解析出的 options 参数
   */
  // private _parseDriverTGLOptions(
  //   buffer: ArrayBuffer,
  //   len: number,
  // ): {
  //   write: boolean
  //   mKey: number[]
  //   key1: number[]
  //   delay: number[]
  // } {
  //   const view = new DataView(buffer)
  //   let offset = 0

  //   // 1. 解析头部
  //   const HEAD = view.getUint8(offset++) // 协议头
  //   const dataLength = view.getUint8(offset++) // 数据长度
  //   const cmd = view.getUint8(offset++) // 命令
  //   //const checksum = view.getUint8(offset++) // 校验和
  //   offset++

  //   // 2. 验证协议头
  //   if (HEAD !== 0xa5) {
  //     throw new Error(`无效的协议头: 期望 0xa5, 实际 0x${HEAD.toString(16)}`)
  //   }

  //   // 3. 解析写入/读取标志
  //   const isWrite = (cmd & 0x80) !== 0
  //   const baseCmd = cmd & 0x7f

  //   // 验证命令是否为 TGL 驱动参数命令
  //   if (baseCmd !== CMD_OHID.ohid_cmd_driver_tgl) {
  //     throw new Error(
  //       `无效的命令: 期望 0x${CMD_OHID.ohid_cmd_driver_tgl.toString(16)}, 实际 0x${baseCmd.toString(16)}`,
  //     )
  //   }

  //   // 4. 验证数据长度
  //   const expectedDataLength = len * 6 // 每个 TGL 项: mKey(2字节) + key1(2字节) + delay(2字节) = 6字节
  //   if (dataLength < expectedDataLength) {
  //     throw new Error(`数据长度不匹配: 期望 ${expectedDataLength}, 实际 ${dataLength}`)
  //   }

  //   // 5. 解析数据数组
  //   const mKey: number[] = []
  //   const key1: number[] = []
  //   const delay: number[] = []

  //   for (let i = 0; i < len; i++) {
  //     if (offset + 6 > view.byteLength) {
  //       throw new Error(`缓冲区越界: 尝试读取位置 ${offset}, 但缓冲区长度只有 ${view.byteLength}`)
  //     }

  //     mKey.push(view.getUint16(offset, true))
  //     offset += 2

  //     key1.push(view.getUint16(offset, true))
  //     offset += 2

  //     delay.push(view.getUint16(offset, true))
  //     offset += 2
  //   }

  //   return {
  //     write: isWrite,
  //     mKey,
  //     key1,
  //     delay,
  //   }
  // }

  /**
   * 生成 DKS 驱动参数数据包
   * 每个 DKS 项包含: mKey(2字节) + key1(2字节) + key2(2字节) + key3(2字节) + key4(2字节) + TrPs1(1字节) + TrPs2(1字节) + TrPs3(1字节) + TrPs4(1字节) + mm1(1字节) = 15字节
   *
   * @param options - 参数选项
   * @returns ArrayBuffer 数据包
   */
  private ohidDriverDKS(options: {
    write: boolean
    mKey: number[]
    key1: number[]
    key2: number[]
    key3: number[]
    key4: number[]
    TrPs1: number[]
    TrPs2: number[]
    TrPs3: number[]
    TrPs4: number[]
    mm1: number[]
    len: number
  }): ArrayBuffer {
    const buffer = new ArrayBuffer(64 * 4)
    const view = new DataView(buffer)

    const cmd = options.write
      ? OHID_CMD_WRITE(CMD_OHID.ohid_cmd_driver_dks)
      : CMD_OHID.ohid_cmd_driver_dks
    let offset = 0

    // 写入头部
    view.setUint8(offset++, MASTER_REPORT_ID) // HEAD
    view.setUint8(offset++, 0) // length
    view.setUint8(offset++, cmd) // cmd
    view.setUint8(offset++, 0) // checksum

    // 写入数据：每个 DKS 项包含 5 个 16 位值和 5 个 8 位值（共 15 字节）
    for (let i = 0; i < options.len; i++) {
      // 5 个 16 位值
      view.setUint16(offset, options.mKey[i] || 0, true)
      offset += 2
      view.setUint16(offset, options.key1[i] || 0, true)
      offset += 2
      view.setUint16(offset, options.key2[i] || 0, true)
      offset += 2
      view.setUint16(offset, options.key3[i] || 0, true)
      offset += 2
      view.setUint16(offset, options.key4[i] || 0, true)
      offset += 2

      // 5 个 8 位值
      view.setUint8(offset, options.TrPs1[i] || 0)
      offset++
      view.setUint8(offset, options.TrPs2[i] || 0)
      offset++
      view.setUint8(offset, options.TrPs3[i] || 0)
      offset++
      view.setUint8(offset, options.TrPs4[i] || 0)
      offset++
      view.setUint8(offset, options.mm1[i] || 0)
      offset++
    }

    // 更新长度
    const dataLength = offset - 4
    view.setUint8(1, dataLength)

    // 计算校验和
    const checksum = calculateChecksum(view, dataLength, 4)
    view.setUint8(3, checksum & 0xff)

    return buffer.slice(0, offset)
  }

  /**
   * ohidDriverDKS 的逆函数
   * 从 ArrayBuffer 解析 DKS 驱动参数配置
   *
   * @param buffer - ArrayBuffer 数据包
   * @param len - 数据项的长度
   * @returns 解析出的 options 参数
   */
  // private _parseDriverDKSOptions(
  //   buffer: ArrayBuffer,
  //   len: number,
  // ): {
  //   write: boolean
  //   mKey: number[]
  //   key1: number[]
  //   key2: number[]
  //   key3: number[]
  //   key4: number[]
  //   TrPs1: number[]
  //   TrPs2: number[]
  //   TrPs3: number[]
  //   TrPs4: number[]
  //   mm1: number[]
  // } {
  //   const view = new DataView(buffer)
  //   let offset = 0

  //   // 1. 解析头部
  //   const HEAD = view.getUint8(offset++) // 协议头
  //   const dataLength = view.getUint8(offset++) // 数据长度
  //   const cmd = view.getUint8(offset++) // 命令
  //   //const checksum = view.getUint8(offset++) // 校验和
  //   offset++

  //   // 2. 验证协议头
  //   if (HEAD !== 0xa5) {
  //     throw new Error(`无效的协议头: 期望 0xa5, 实际 0x${HEAD.toString(16)}`)
  //   }

  //   // 3. 解析写入/读取标志
  //   const isWrite = (cmd & 0x80) !== 0
  //   const baseCmd = cmd & 0x7f

  //   // 验证命令是否为 DKS 驱动参数命令
  //   if (baseCmd !== CMD_OHID.ohid_cmd_driver_dks) {
  //     throw new Error(
  //       `无效的命令: 期望 0x${CMD_OHID.ohid_cmd_driver_dks.toString(16)}, 实际 0x${baseCmd.toString(16)}`,
  //     )
  //   }

  //   // 4. 验证数据长度
  //   // 每个 DKS 项: mKey(2) + key1(2) + key2(2) + key3(2) + key4(2) + TrPs1(1) + TrPs2(1) + TrPs3(1) + TrPs4(1) + mm1(1) = 15字节
  //   const expectedDataLength = len * 15
  //   if (dataLength < expectedDataLength) {
  //     throw new Error(`数据长度不匹配: 期望 ${expectedDataLength}, 实际 ${dataLength}`)
  //   }

  //   // 5. 解析数据数组
  //   const mKey: number[] = []
  //   const key1: number[] = []
  //   const key2: number[] = []
  //   const key3: number[] = []
  //   const key4: number[] = []
  //   const TrPs1: number[] = []
  //   const TrPs2: number[] = []
  //   const TrPs3: number[] = []
  //   const TrPs4: number[] = []
  //   const mm1: number[] = []

  //   for (let i = 0; i < len; i++) {
  //     if (offset + 15 > view.byteLength) {
  //       throw new Error(`缓冲区越界: 尝试读取位置 ${offset}, 但缓冲区长度只有 ${view.byteLength}`)
  //     }

  //     // 读取 5 个 16 位值
  //     mKey.push(view.getUint16(offset, true))
  //     offset += 2

  //     key1.push(view.getUint16(offset, true))
  //     offset += 2

  //     key2.push(view.getUint16(offset, true))
  //     offset += 2

  //     key3.push(view.getUint16(offset, true))
  //     offset += 2

  //     key4.push(view.getUint16(offset, true))
  //     offset += 2

  //     // 读取 5 个 8 位值
  //     TrPs1.push(view.getUint8(offset))
  //     offset++

  //     TrPs2.push(view.getUint8(offset))
  //     offset++

  //     TrPs3.push(view.getUint8(offset))
  //     offset++

  //     TrPs4.push(view.getUint8(offset))
  //     offset++

  //     mm1.push(view.getUint8(offset))
  //     offset++
  //   }

  //   return {
  //     write: isWrite,
  //     mKey,
  //     key1,
  //     key2,
  //     key3,
  //     key4,
  //     TrPs1,
  //     TrPs2,
  //     TrPs3,
  //     TrPs4,
  //     mm1,
  //   }
  // }
  /**
   * 异步分批从设备获取键位数据并存入 buffer
   * 改进版本：更好的错误处理、进度回调、可取消
   *
   * @param allKeys 所有键位的 key 数组
   * @param batchSize 每批处理的键位数
   * @param onProgress 进度回调函数
   * @param signal 取消信号
   * @returns 包含所有键位数据的 ArrayBuffer
   */
  async batchGetKeyDataFromDeviceAsync(
    allKeys: number[],
    batchSize: number = 12,
    item: number,
    onProgress?: (progress: {
      batchIndex: number
      batchCount: number
      processed: number
      total: number
      percent: number
    }) => void,
    signal?: AbortSignal,
  ): Promise<ArrayBuffer> {
    const totalKeys = allKeys.length

    // 计算总批次数
    const batchCount = Math.ceil(totalKeys / batchSize)

    // 创建结果 buffer
    const resultBuffer = new ArrayBuffer(totalKeys * 3)
    const resultView = new DataView(resultBuffer)
    let resultOffset = 0

    console.log(
      `异步处理开始: 总键位数=${totalKeys}, 批次大小=${batchSize}, 总批次数=${batchCount}`,
    )

    // 检查取消信号
    if (signal?.aborted) {
      throw new Error('操作已被取消')
    }

    // 使用 Promise.allSettled 并行处理批次（可选）
    // 或者使用 for 循环串行处理以保证顺序

    for (let batchIndex = 0; batchIndex < batchCount; batchIndex++) {
      // 检查取消信号
      if (signal?.aborted) {
        console.log('操作被用户取消')
        throw new Error('操作已被取消')
      }

      const startIdx = batchIndex * batchSize
      const endIdx = Math.min(startIdx + batchSize, totalKeys)
      const currentBatchSize = endIdx - startIdx

      // 触发进度回调
      if (onProgress) {
        onProgress({
          batchIndex: batchIndex + 1,
          batchCount,
          processed: startIdx + currentBatchSize,
          total: totalKeys,
          percent: Math.round(((batchIndex + 1) / batchCount) * 100),
        })
      }

      console.log(`处理批次 ${batchIndex + 1}/${batchCount} (${currentBatchSize} 个键位)`)

      try {
        // 处理当前批次
        await this.processSingleBatchAsync(
          allKeys.slice(startIdx, endIdx),
          batchIndex,
          resultView,
          resultOffset,
          item,
          signal,
        )

        resultOffset += currentBatchSize * 3

        console.log(`批次 ${batchIndex + 1} 处理完成`)
      } catch (error) {
        console.error(`批次 ${batchIndex + 1} 处理失败:`, error)
        throw new Error(`批次 ${batchIndex + 1} 失败: ${error}`)
      }

      // 批次间延迟（可配置）
      if (batchIndex < batchCount - 1) {
        await this.delayAsync(50) // 异步延迟
      }
    }

    console.log(`异步处理完成: 总共处理 ${totalKeys} 个键位`)
    return resultBuffer
  }

  /**
   * 异步处理单个批次
   */
  async processSingleBatchAsync(
    batchKeys: number[],
    _batchIndex: number,
    resultView: DataView,
    resultOffset: number,
    item: number,
    signal?: AbortSignal,
  ): Promise<void> {
    const batchSize = batchKeys.length

    // 检查取消信号
    if (signal?.aborted) {
      return Promise.reject(new Error('操作已被取消'))
    }

    // 准备请求参数
    const requestOptions = {
      write: false,
      mKey: batchKeys,
      item: Array(batchSize).fill(item),
      value: Array(batchSize).fill(0xffff),
      len: batchSize,
    }

    // 生成请求包
    const packetInfo = this.ohidDriverParam(requestOptions)
    const dataView = new DataView(packetInfo)

    // 准备发送数据
    const paddedData = new Uint8Array(dataView.buffer, 0, dataView.byteLength)
    const outputData = new Uint8Array(64)
    outputData.set(paddedData, 0)
    //获取 report ID
    const outputReportId = REPORT_ID
    const inputReportId = REPORT_ID

    // 发送请求（异步）
    const response = await this.device.sendAndReceive(
      outputReportId,
      outputData,
      inputReportId,
      500,
    )

    // 解析响应
    const responseOptions = await this.parseDriverParamOptions(
      response.data.buffer as ArrayBuffer,
      batchSize,
    )

    // 将数据写入结果 buffer
    let offset = resultOffset
    for (let i = 0; i < batchSize; i++) {
      resultView.setUint8(offset, responseOptions.item[i] || 0)
      offset++
      resultView.setUint16(offset, responseOptions.value[i] || 0, true)
      offset += 2
    }
  }

  /**
   * 异步延迟函数
   */
  private delayAsync(ms: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, ms)
    })
  }
  /**
   * 获取当前层键位映射
   * 自动区分windows和macOS，根据系统不同读取不同地址
   * @param isFN 是否为Fn层
   */
  async getLayerKeysMap(isFN?: boolean, _platform?: platform): Promise<LayerKeysMap> {
    // 获取所有键位
    const allKeys = this.device.manifest.layout.keys.flatMap((row) => row.map((key) => key.key))

    // 创建取消控制器
    const controller = new AbortController()

    // 进度回调
    const onProgress = (progress: any) => {
      console.log(`进度: ${progress.percent}% (${progress.processed}/${progress.total})`)
    }

    // 设置超时
    const timeoutId = setTimeout(() => {
      controller.abort()
      console.log('操作超时，已取消')
    }, 30000) // 30秒超时

    // 异步获取常规按键数据
    const allKeysData = await this.batchGetKeyDataFromDeviceAsync(
      allKeys,
      12, // 每批12个
      isFN ? 1 : 0,
      onProgress,
      controller.signal,
    )
    //获取按键工作模式，判断是普通还是高级键
    let modeView: DataView = new DataView(new ArrayBuffer(64))
    if (!isFN) {
      // 异步获取数据
      const allKeysMode = await this.batchGetKeyDataFromDeviceAsync(
        allKeys,
        12, // 每批12个
        0x04,
        onProgress,
        controller.signal,
      )
      modeView = new DataView(allKeysMode)
    }

    clearTimeout(timeoutId)

    console.log('数据获取完成')

    const layerKeysMap = [] as LayerKeysMap

    for (let i = 0; i < allKeys.length; i++) {
      const startOffset = i * 3
      // 创建当前键位的 DataView（3个字节）
      const keyDataBuffer = allKeysData.slice(startOffset, startOffset + 3)
      const keyDataView = new DataView(keyDataBuffer)

      // 根据判断结果决定第二个参数
      let type = 1
      // 判断后两字节的高位是否为 0xC000
      // 后两字节：从索引1开始的2个字节（小端序）
      const highByte = keyDataView.getUint16(1, true) // 读取后两字节

      if (highByte === 1) continue

      if ((highByte & 0xff00) === 0x0000) {
        type = 0x01 //普通键
      } else if ((highByte & 0xf000) === 0xc000) {
        type = 0x02 //多媒体
      } else if ((highByte & 0xf000) === 0xf000) {
        type = 0x09 //功能键
      } else if ((highByte & 0xf000) === 0x6000) {
        type = 0x04 //鼠标
      }

      let mode = 0
      let keyAttr = {} as keyAttr
      if (!isFN) {
        mode = modeView.getUint16(startOffset + 1, true)
      }
      if (mode === 0) {
        //常规按键
        keyAttr = this.disposeRead(keyDataView, type)
      } else {
        if (mode === 1) {
          const mtBuffer = await this.getDriverMT(allKeys[i])
          keyAttr = this.disposeRead(mtBuffer, 0x0a)
        } else if (mode === 2) {
          const tglBuffer = await this.getDriverTGL(allKeys[i])
          keyAttr = this.disposeRead(tglBuffer, 0x0b)
        } else if (mode === 0x20) {
          const dksBuffer = await this.getDriverDKS(allKeys[i])
          keyAttr = this.disposeRead(dksBuffer, 0x0c)
        }
      }
      const keyItem: keyMapItem = {
        index: allKeys[i],
        keyAttr,
      }
      layerKeysMap.push(keyItem)
    }

    return layerKeysMap
  }

  /**
   * TODO 说是其他主控也差不多一样的数据结构，但没做到以前先不抽出去，后续做到了再看是否真一致
   * 因为说能做到一样的数据原因是和固件battle后的结果，是否真遵守这里先存疑
   * 下面的发送前处理disposeSend也是一样不要急着先抽
   */
  // 键盘数据处理器
  private disposeRead(result: DataView, type: number): keyAttr {
    const data = {} as keyAttr
    // 功能类型
    data.type = type
    switch (type) {
      case 0x00:
        // 禁用键
        break
      case 0x01:
        // 键盘普通键
        // const length = result.getUint8(5) // 字节5：HID键数量
        // data.modifyKey = result.getUint8(6) // 字节6：Modify_key修饰键
        // // 读取最多6个HID键码（字节7~12），按实际长度读取
        // data.code = []
        // for (let i = 0; i < Math.min(length, 6); i++) {
        //   data.code.push(result.getUint8(7 + i))
        // }
        data.code = []
        data.code.push(result.getUint8(1))
        break
      case 0x02:
        // 键盘多媒体键 小端开始读取两位（低位+高位）
        data.code = [result.getUint16(1, true) & 0x0fff]
        break
      case 0x03:
        // 键盘电源键
        data.code = [result.getUint8(5)]
        break
      case 0x04:
        // 鼠标功能
        const mouseBtn = result.getUint16(1, true) // 协议字节2：Mouse_key 鼠标按键位映射

        // 解析鼠标按键（bit0~bit4 对应 Left/Right/Middle/Button4/Button5）
        const mouseKey: mouseKey = [
          mouseBtn === 0x6100 ? 1 : 0, // 下标0: 左键 (bit0)
          mouseBtn === 0x6200 ? 1 : 0, // 下标1: 右键 (bit1)
          mouseBtn === 0x6300 ? 1 : 0, // 下标2: 中键 (bit2)
          mouseBtn === 0x6400 ? 1 : 0, // 下标3: 侧键4 (bit3)
          mouseBtn === 0x6500 ? 1 : 0, // 下标4: 侧键5 (bit4)
        ]

        const x = 0
        const y = 0
        const verticalWheel = 0
        const horizontalWheel = 0

        // 组装mouse对象到keyAttr，兼容原字段命名
        data.mouse = {
          mouseKey,
          x,
          y,
          colWheelNum: verticalWheel,
          rowWheelNum: horizontalWheel,
        }
        break
      case 0x05:
        // 鼠标键功能
        const mouseKeyByte = result.getUint8(5) // 字节5：Mouse_key 按键位映射
        // 解析鼠标按键（bit0~bit4 对应 Left/Right/Middle/Button4/Button5）
        const parsedMouseKey: mouseKey = [
          (mouseKeyByte >> 0) & 0x01, // 左键 bit0
          (mouseKeyByte >> 1) & 0x01, // 右键 bit1
          (mouseKeyByte >> 2) & 0x01, // 中键 bit2
          (mouseKeyByte >> 3) & 0x01, // Button4 bit3
          (mouseKeyByte >> 4) & 0x01, // Button5 bit4
        ]
        // 字节6：点击次数
        const clickNum = result.getUint8(6)
        // 字节7~8：press_Time uint16小端序，单位ms
        const pressTime = result.getUint16(7, true)
        // 字节9~10：relase_Time uint16小端序，单位ms
        const relaseTime = result.getUint16(9, true)

        // 组装到data
        data.mouseKey = {
          mouseKey: parsedMouseKey,
          clickNum,
          pressTime,
          relaseTime,
        }
        break
      case 0x06:
      case 0x07:
        // 鼠标滚轮功能（0x06垂直 / 0x07水平）
        const wheelType = result.getUint8(5) // 字节5：滚轮类型
        const wheelNumber = result.getInt8(6) // 字节6：每次滚动格数
        const wheelTime = result.getUint16(7, true) // 字节7~8：间隔时间（小端序）

        // 统一组装wheel对象
        const wheelData = {
          type: wheelType,
          number: wheelNumber,
          time: wheelTime,
        }

        // 根据功能码赋值给对应字段
        if (data.type === 0x06) {
          data.colWheel = wheelData
        } else {
          data.rowWheel = wheelData
        }
        break
      case 0x08:
        // 宏定义功能
        const macro: macro = {
          loop: result.getUint16(5, true),
          index: result.getUint8(7),
        }
        data.macro = macro
        break
      case 0x09:
        // 功能切换键
        const code = result.getUint16(1, true)
        if (code === 0xf202) {
          data.code = [5]
        } else if (code === 0xf203) {
          data.code = [6]
        } else if (code === 0xf204) {
          data.code = [8]
        } else if (code === 0xf205) {
          data.code = [9]
        } else if (code === 0xf001) {
          data.code = [0x01]
        } else if (code === 0xf101) {
          data.code = [0x14]
        } else {
          data.code = [result.getUint16(1)]
          console.log(`按键键码： 0x${code.toString(16)}`)
        }
        //data.code = [result.getUint8(5)]
        //data.confIndex = result.getUint8(6)
        break
      case 0x0a:
        // 按住/单击 MT 功能
        const dualMTKey: DualModeKey = {
          // 1. 解析长按时间：字节10-11
          time: result.getUint16(10, true) * 10,
          // 2. 解析单击键：字节7=类型，字节8=键码（文档里单击键最长3字节，基础场景取字节8）
          clickType: 0,
          clickKeyCode: result.getUint16(8, true),
          // 3. 解析长按键：字节9=类型，字节10=键码（长按键最长3字节，基础场景取字节10）
          longPressType: 0,
          longPressKeyCode: result.getUint16(6, true),
        }
        data.dualModeKey = dualMTKey
        break
      case 0x0b:
        // 切换开关(TGL)功能
        const dualTGLKey: DualModeKey = {
          // 1. 解析长按时间：字节10-11
          time: result.getUint16(8, true) * 10,
          // 2. 解析单击键：字节7=类型，字节8=键码（文档里单击键最长3字节，基础场景取字节8）
          clickType: 0,
          clickKeyCode: result.getUint16(6, true),
          // 3. 解析长按键：字节9=类型，字节10=键码（长按键最长3字节，基础场景取字节10）
          longPressType: 0,
          longPressKeyCode: result.getUint16(6, true),
        }
        data.dualModeKey = dualTGLKey
        break
      case 0x0c:
        // 动态键程(DKS)功能
        const dks: DKS = {
          actPoint1: result.getUint8(22),
          actPoint2: 0,
          releasePoint1: result.getUint8(22),
          releasePoint2: 0,
          triggerBits: [],
          keyCodes: [],
        }

        dks.triggerBits = [
          this.inverseConvertTriggerByte(result.getUint8(14)), // 键码1触发位
          this.inverseConvertTriggerByte(result.getUint8(15)), // 键码2触发位
          this.inverseConvertTriggerByte(result.getUint8(16)), // 键码3触发位
          this.inverseConvertTriggerByte(result.getUint8(17)), // 键码4触发位
        ]

        // 读取4个键码
        dks.keyCodes = [
          result.getUint16(6, true), // 键码1
          result.getUint16(8, true), // 键码2
          result.getUint16(10, true), // 键码3
          result.getUint16(12, true), // 键码4
        ]

        data.dks = dks
        break
      case 0x0d:
        // RS 功能
        const rs = {} as RS
        rs.combineRT = result.getUint8(5) // 字节5：结合RT (0=不结合/1=结合)
        rs.relatedKeyRow = result.getUint8(6) // 字节6：关联键索引1
        rs.relatedKeyCol = result.getUint8(7) // 字节7：关联键索引2
        rs.triggerKeyType = result.getUint8(8) as fnKeyType // 字节8：触发键类型
        rs.triggerKeyCode = result.getUint8(9) // 字节9：触发键码（修饰键/HID码）
        rs.triggerKeyExtCode = result.getUint8(10) // 字节10：触发键扩展码
        rs.triggerThreshold = result.getUint16(11, true) // 字节11：RS触发阈值（0.01mm）
        data.rs = rs
        break
      case 0x0e:
        // SOCD
        const socd = {} as SOCD
        socd.subMode = result.getUint8(5) // 字节5：SOCD子模式
        socd.combineRT = result.getUint8(6) // 字节6：结合RT (0=不结合/1=结合)
        socd.triggerWhenBottom = result.getUint8(7) // 字节7：按到底都触发
        socd.relatedKeyRow = result.getUint8(8) // 字节8：关联键行索引
        socd.relatedKeyCol = result.getUint8(9) // 字节9：关联键列索引
        socd.triggerKeyType = result.getUint8(10) as fnKeyType // 字节10：触发键类型（功能码）
        socd.triggerKeyCode = result.getUint8(11) // 字节11：触发键码（键码1）
        socd.triggerKeyExtCode = result.getUint8(12) // 字节12：触发键扩展码（键码2）
        socd.selfPriority = result.getUint8(13) // 字节13：本键绝对优先
        // 触发阈值：字节14~15（小端序uint16，单位0.01mm）
        socd.triggerThreshold = result.getUint16(14, true)
        data.socd = socd
        break
      case 0x0f:
        // TODO 鼠标和键盘组合键功能（仅限旋钮定义更改）
        break
    }
    return data
  }

  /**
   * 设置当前层键位映射
   * 自动区分windows和macOS，根据系统不同读取不同地址
   * @param keysMap 键位映射
   * @param isFN 是否为Fn层
   */
  async setLayerKeysMap(keysMap: keyMapItem, isFN?: boolean, _platform?: platform): Promise<void> {
    const key = keysMap.index
    //获取 report ID
    const outputReportId = REPORT_ID
    const inputReportId = REPORT_ID
    const outputData = this.disposeSend(key, keysMap.keyAttr, isFN)

    // 发送请求（异步）
    await this.device.sendAndReceive(outputReportId, outputData, inputReportId, 500)
    return
  }

  // 发送前处理
  private disposeSend(key: number, data: keyAttr, isFN?: boolean): Uint8Array {
    const outputData = new Uint8Array(64)

    const buffer = new ArrayBuffer(64)
    const dv = new DataView(buffer)

    // 字节0：功能类型
    const type = data.type ?? 0x00
    switch (type) {
      case 0x00:
        // 禁用键
        break
      case 0x01:
        // 键盘普通键
        if (data.code?.length) {
          // 准备请求参数
          const requestOptions = {
            write: true,
            mKey: Array(1).fill(key),
            item: Array(1).fill(isFN ? 0x01 : 0x00),
            value: [data.code[0]],
            len: 1,
          }
          outputData.set(new Uint8Array(this.ohidDriverParam(requestOptions)), 0)
        }
        break
      case 0x02:
        // 键盘多媒体键
        if (data.code?.length) {
          let combinedCode = 0
          for (let i = 0; i < data.code.length; i++) {
            combinedCode = (combinedCode << 8) | data.code[i]
          }
          combinedCode = combinedCode | 0xc000
          const requestOptions = {
            write: true,
            mKey: Array(1).fill(key),
            item: Array(1).fill(isFN ? 0x01 : 0x00),
            value: [combinedCode],
            len: 1,
          }
          outputData.set(new Uint8Array(this.ohidDriverParam(requestOptions)), 0)
        }
        break
      case 0x03:
        // 键盘电源键
        break
      case 0x04:
        // 鼠标功能
        const mouse = data.mouse
        if (mouse) {
          // 1. 协议字节2 (result索引1)：鼠标按键位映射
          let mouseBtnByte = 0x00
          const mouseKey = mouse.mouseKey ?? [0, 0, 0, 0, 0]
          if (mouseKey[0]) mouseBtnByte = 0x6100 // 左键 (bit0)
          if (mouseKey[1]) mouseBtnByte = 0x6200 // 右键 (bit1)
          if (mouseKey[2]) mouseBtnByte = 0x6300 // 中键 (bit2)
          if (mouseKey[3]) mouseBtnByte = 0x6400 // 侧键4 (bit3)
          if (mouseKey[4]) mouseBtnByte = 0x6500 // 侧键5 (bit4)
          const requestOptions = {
            write: true,
            mKey: Array(1).fill(key),
            item: Array(1).fill(isFN ? 0x01 : 0x00),
            value: [mouseBtnByte],
            len: 1,
          }
          outputData.set(new Uint8Array(this.ohidDriverParam(requestOptions)), 0)
        }
        break
      case 0x05:
        break
      case 0x06:
      case 0x07:
        break
      case 0x08:
        // 宏定义功能
        break
      case 0x09:
        // 功能切换键
        if (data.code?.length) {
          dv.setUint8(1, data.code[0])
        }
        dv.setUint8(2, data.confIndex ?? 0x00)
        break
      case 0x0a:
        // 按住/单击 MT 功能
        const dualMTKey: DualModeKey = data.dualModeKey as DualModeKey
        if (dualMTKey) {
          // 1. 解析长按时间
          const delay = dualMTKey.time / 10
          // 2. 解析单击键：字节3=类型，字节4-5=键码
          const clickKeyCode = dualMTKey.clickKeyCode
          const longPressKeyCode = dualMTKey.longPressKeyCode
          const requestOptions = {
            write: true,
            mKey: Array(1).fill(key),
            key1: Array(1).fill(longPressKeyCode),
            key2: Array(1).fill(clickKeyCode),
            delay: [delay],
            len: 1,
          }
          outputData.set(new Uint8Array(this.ohidDriverMT(requestOptions)), 0)
        }
        break
      case 0x0b:
        // 切换开关(TGL)功能
        const dualDKSModeKey: DualModeKey = data.dualModeKey as DualModeKey
        if (dualDKSModeKey) {
          const delay = dualDKSModeKey.time / 10
          const clickKeyCode = dualDKSModeKey.clickKeyCode

          const requestOptions = {
            write: true,
            mKey: Array(1).fill(key),
            key1: Array(1).fill(clickKeyCode),
            delay: [delay],
            len: 1,
          }
          outputData.set(new Uint8Array(this.ohidDriverTGL(requestOptions)), 0)
        }
        break
      case 0x0c:
        // 动态键程(DKS)功能
        const dks: DKS = data.dks as DKS
        if (dks) {
          const KEY1 = dks.keyCodes[0]
          const KEY2 = dks.keyCodes[1]
          const KEY3 = dks.keyCodes[2]
          const KEY4 = dks.keyCodes[3]
          const TrPs1 = this.convertTriggerByte(dks.triggerBits[0])
          const TrPs2 = this.convertTriggerByte(dks.triggerBits[1])
          const TrPs3 = this.convertTriggerByte(dks.triggerBits[2])
          const TrPs4 = this.convertTriggerByte(dks.triggerBits[3])
          const mm1 = dks.actPoint1 / 100 / 0.02
          const requestOptions = {
            write: true,
            mKey: Array(1).fill(key),
            key1: Array(1).fill(KEY1),
            key2: Array(1).fill(KEY2),
            key3: Array(1).fill(KEY3),
            key4: Array(1).fill(KEY4),
            TrPs1: Array(1).fill(TrPs1),
            TrPs2: Array(1).fill(TrPs2),
            TrPs3: Array(1).fill(TrPs3),
            TrPs4: Array(1).fill(TrPs4),
            mm1: Array(1).fill(mm1),
            len: 1,
          }
          outputData.set(new Uint8Array(this.ohidDriverDKS(requestOptions)), 0)
        }
        break
      case 0x0d:
        // RS 功能
        break
      case 0x0e:
        // SOCD
        break
      case 0x0f:
        // TODO 鼠标和键盘组合键功能（仅限旋钮定义更改）
        break
    }

    // 转换为Uint8Array返回
    return outputData
  }

  /**
   * 将 triggerBits 中的一个字节转换为 DKS 模式触发状态
   * @param {number} triggerByte - 输入的字节（0~255），按每 2 bit 一组存放 4 个触发点状态
   * @returns {number} 组合后的 DKS 触发状态掩码
   */
  private convertTriggerByte(triggerByte: number) {
    //OHID(DKS,PRESS)                     = 0x01, // 按下
    //OHID(DKS,PRESS_HOLD)                = 0x02, // 按下保持
    //OHID(DKS,PRESS_BOTTON)              = 0x04, // 触底
    //OHID(DKS,BOTTON_HOLD)               = 0x18, // 触底保持
    //OHID(DKS,UP)                        = 0x20, // 抬手
    //OHID(DKS,UP_HOLD)                   = 0x40, // 抬手保持
    //OHID(DKS,FREE)                      = 0x80, // 完全释放
    const n0 = triggerByte & 0x03
    const n1 = (triggerByte >> 2) & 0x03
    const n2 = (triggerByte >> 4) & 0x03
    const n3 = (triggerByte >> 6) & 0x03

    let TrPs = 0x00

    if (n0 === 1) {
      TrPs += 0x01 + 0x02
    } else if (n0 === 3) {
      TrPs += 0x01
    }

    if (n1 === 1) {
      TrPs += 0x04 + 0x18
    } else if (n1 === 3) {
      TrPs += 0x04
    }

    if (n2 === 1) {
      TrPs += 0x20 + 0x40
    } else if (n2 === 3) {
      TrPs += 0x20
    }

    if (n3 > 0) {
      TrPs += 0x80
    }

    return TrPs
  }

  /**
   * convertTriggerByte 的逆函数
   * 将 DKS 触发状态掩码还原为原始的 triggerByte
   * @param {number} TrPs1 - DKS 触发状态掩码
   * @returns {number} 还原后的 triggerByte（0~255）
   */
  private inverseConvertTriggerByte(TrPs: number) {
    let n0 = 0
    let n1 = 0
    let n2 = 0
    let n3 = 0

    // 解析 n0（最低两位）：完全释放
    if (TrPs & 0x80) {
      n3 = 3 // 有事件，标记为按下保持
    }

    // 解析 n1：抬手 / 抬手保持
    if (TrPs & 0x40) {
      n2 = 1 // 抬手保持 → 未抬起
    } else if (TrPs & 0x20) {
      n2 = 3 // 仅抬手 → 已抬起
    }

    // 解析 n2：触底 / 触底保持
    if (TrPs & 0x18) {
      n1 = 1 // 触底保持 → 未抬起
    } else if (TrPs & 0x04) {
      n1 = 3 // 仅触底 → 已抬起
    }

    // 解析 n3（最高两位）：按下 / 按下保持
    if (TrPs & 0x02) {
      n0 = 1 // 按下保持 → 未抬起
    } else if (TrPs & 0x01) {
      n0 = 3 // 仅按下 → 已抬起
    }

    // 组合回一个字节
    const triggerByte = (n3 << 6) | (n2 << 4) | (n1 << 2) | n0
    return triggerByte
  }

  // 获取宏列表
  async getMacroList(): Promise<Macro[]> {
    throw new Error('OHIDKeys does not support getMacroList')
  }

  // private get _usedForRef() {
  //   void this._parseDriverMTOptions
  //   void this._parseDriverTGLOptions
  //   void this._parseDriverDKSOptions
  //   return undefined
  // }
}
